import { useCallback } from "react"
import { useDispatch } from "react-redux"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { DefaultError } from "@tanstack/react-query"
import { queryStatus } from "helpers/data-statuses"
import type { DataStatus } from "helpers/data-statuses"
import { useNotifications } from "hooks/notifications"
import { useGetQuery } from "hooks/queries"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"

export interface AiSettingsResponse {
  readOnly: boolean
}

export function readOnlyLabel(readOnly: boolean): string {
  return readOnly ? "Read only" : "Read and write"
}

export function getAiSettingsQueryKey(accountAlias: string): [string, string] {
  return ["ai-settings", accountAlias]
}

export function useAiSettings(): { status: DataStatus, data: AiSettingsResponse | undefined } {
  const accountAlias = useAccountAlias()
  const query = useGetQuery<AiSettingsResponse>({ url: `/api/${accountAlias}/ai/settings`, key: getAiSettingsQueryKey(accountAlias) })
  return { status: queryStatus(query), data: query.data }
}

interface ReadOnlyChange {
  from: boolean | undefined
  to: boolean
}

export interface ReadOnlySaved {
  oldValue: boolean
  newValue: boolean
}

export function useSetReadOnly(): { setReadOnly: (to: boolean) => Promise<ReadOnlySaved>, isSaving: boolean } {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const notifs = useNotifications()
  const accountAlias = useAccountAlias()
  const key = getAiSettingsQueryKey(accountAlias)

  const { mutateAsync, isPending } = useMutation<ReadOnlySaved, DefaultError, ReadOnlyChange>({
    mutationFn: (change) =>
      new Promise((resolve, reject) => {
        // Two options only, so a click with nothing cached still started from the other one.
        const oldValue = change.from ?? !change.to
        dispatch(Creators.patch(`/api/${accountAlias}/ai/settings`, { readOnly: change.to }, () => { resolve({ oldValue, newValue: change.to }) }, reject))
      }),
    onMutate: async ({ to }) => {
      await queryClient.cancelQueries({ queryKey: key })
      queryClient.setQueryData<AiSettingsResponse>(key, (current) => (current ? { ...current, readOnly: to } : current))
    },
    onError: (_error, { from, to }) => {
      const previous = from ?? !to
      queryClient.setQueryData<AiSettingsResponse>(key, (current) => (current ? { ...current, readOnly: previous } : current))
      notifs.error(`Could not change Omega Point access. The save failed, so it is still ${readOnlyLabel(previous)}.`)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key })
    },
  })

  const setReadOnly = useCallback((to: boolean) => {
    const from = queryClient.getQueryData<AiSettingsResponse>(getAiSettingsQueryKey(accountAlias))?.readOnly
    return mutateAsync({ from, to })
  }, [queryClient, accountAlias, mutateAsync])

  return { setReadOnly, isSaving: isPending }
}
